import React, { useMemo, useState } from "react";
import { StreamChat } from "stream-chat";
import { useTeams } from "@/components/pages/staff/hooks/useTeams";
import { useCreateInternalChannel } from "@/components/pages/chats/hooks/useChannel";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface InternalTeamsBarProps {
  client: StreamChat;
  currentUserId: string;
  activeChannelId?: string | null;
}

export const InternalTeamsBar: React.FC<InternalTeamsBarProps> = ({
  client,
  currentUserId,
  activeChannelId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pendingTeamId, setPendingTeamId] = useState<string | null>(null);
  const { mutateAsync: createInternalChannel } = useCreateInternalChannel();
  const { data: teamsData, isLoading } = useTeams({
    isActive: true,
    currentPage: 1,
    perPage: 100,
    sortBy: null,
    sortOrder: null,
  });

  const teams = useMemo(() => teamsData?.data || [], [teamsData?.data]);

  const openTeamChannel = async (team: (typeof teams)[number]) => {
    if (!client || !currentUserId) return;

    const targetUserIds = Array.from(
      new Set(
        (team.userTeams || [])
          .map((item) => item.staffUser.profileId || item.staffUser.id)
          .filter((id) => id && id !== currentUserId),
      ),
    );

    if (!targetUserIds.length) {
      toast.error(t("chats.noUsersFound"));
      return;
    }

    try {
      setPendingTeamId(team.id);

      const matchedChannels = await client.queryChannels(
        {
          type: "messaging",
          members: { $in: [currentUserId] },
          name: { $eq: team.name },
        } as any,
        { last_message_at: -1 },
        { watch: false, state: true, presence: false },
      );

      const exactChannel = matchedChannels.find((channel) => {
        const memberIds = Object.values(channel.state.members)
          .map((member) => member.user?.id)
          .filter(Boolean)
          .filter((id) => id !== currentUserId)
          .sort();

        return (
          JSON.stringify(memberIds) ===
          JSON.stringify([...targetUserIds].sort())
        );
      });

      const existingChannel = exactChannel || matchedChannels[0];

      if (existingChannel?.id) {
        navigate(`/internal-chats?activeChannel=${existingChannel.id}`);
        return;
      }

      const createdChannel = await createInternalChannel({
        targetUserIds,
        chat_type: "group",
        name: team.name,
      });

      if (createdChannel?.id) {
        navigate(`/internal-chats?activeChannel=${createdChannel.id}`);
      }
    } catch (error) {
      console.error("Failed to open team channel:", error);
      toast.error(t("chats.errorCreate"));
    } finally {
      setPendingTeamId(null);
    }
  };

  const handleTeamClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const team = teams.find(
      (candidate) => candidate.id === event.currentTarget.dataset.teamId,
    );
    if (team) {
      void openTeamChannel(team);
    }
  };

  if (isLoading || teams.length === 0) {
    return null;
  }

  return (
    <div className="flex h-[50px] shrink-0 items-center gap-2 overflow-hidden border-b border-neutral-200 bg-white px-4">
      <span className="shrink-0 text-xs font-medium text-neutral-500">
        {t("chats.team")}:
      </span>
      <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto py-1">
        {teams.map((team) => {
          const isActive =
            activeChannelId &&
            client.activeChannels[activeChannelId]?.data?.name === team.name;

          return (
            <Button
              key={team.id}
              type="button"
              data-team-id={team.id}
              variant={isActive ? "new_secondary" : "new_outline"}
              onClick={handleTeamClick}
              disabled={pendingTeamId === team.id}
              className={cn(
                "h-7 max-w-[180px] shrink-0 justify-start rounded-lg px-2 text-xs",
                isActive && "border-royal-glow-3 border",
              )}
            >
              <span
                className={cn(
                  "flex size-4 items-center justify-center rounded-full text-[10px] font-medium text-white",
                  isActive ? "bg-royal-glow-3" : "bg-skyline-2",
                )}
              >
                {team._count?.userTeams ?? team.userTeams?.length ?? 0}
              </span>
              <span className="truncate text-xs font-medium">{team.name}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};
